import React, { useState } from "react";
import { Modal, Button } from "@veneer/core";
import {
  IconCalendarMonth,
  IconReload,
  IconTrash,
} from "@veneer/core";
import { useTranslation } from "react-i18next";
import { ICalendarEvent, DeleteOccurrenceMode } from "./agendaCalendarUtils";
import { AppointmentTypeEnum } from "../../models/enum/AgendaApi/AppointmentTypeEnum";

interface RecurrenceActionModalProps {
  show: boolean;
  event: ICalendarEvent | null;
  canEditOccurrences: boolean;
  /** False for legacy agendas that have no pre-computed occurrence IDs -- all 3 delete modes require them. */
  canDeleteOccurrence: boolean;
  onClose: () => void;
  onEditSeries: (event: ICalendarEvent) => void;
  onEditOccurrences: (event: ICalendarEvent) => void;
  onDeleteSeries: (event: ICalendarEvent) => void;
  onDeleteOccurrence: (event: ICalendarEvent, mode: DeleteOccurrenceMode) => void;
}

const RecurrenceActionModal: React.FC<RecurrenceActionModalProps> = ({
  show,
  event,
  canEditOccurrences,
  canDeleteOccurrence,
  onClose,
  onEditSeries,
  onEditOccurrences,
  onDeleteSeries,
  onDeleteOccurrence,
}) => {
  const [t] = useTranslation("global");
  const [showDeleteOccurrenceModal, setShowDeleteOccurrenceModal] = useState(false);

  if (!event) return null;

  const subtitle = `${event.resource.pointOfSale} - ${event.resource.appointmentType}`;

  const isNonRecurring =
    event.resource.appointmentTypeID === AppointmentTypeEnum.Visit ||
    event.resource.appointmentTypeID === AppointmentTypeEnum.VisitAlt;

  const deleteSeriesLabel = isNonRecurring
    ? t("header.deleteAgendaButton")
    : t("header.deleteSeries");

  const editSeriesLabel = isNonRecurring
    ? t("header.editAgendaButton")
    : t("header.editSeries");

  const handleDeleteOccurrenceClick = () => {
    setShowDeleteOccurrenceModal(true);
  };

  const handleCloseDeleteOccurrenceModal = () => {
    setShowDeleteOccurrenceModal(false);
  };

  const handleOccurrenceMode = (mode: DeleteOccurrenceMode) => {
    setShowDeleteOccurrenceModal(false);
    onClose();
    onDeleteOccurrence(event, mode);
  };

  return (
    <div style={{ position: "relative" }}>
      <Modal
        align="center"
        closeButton
        id="modal-recurrence-action"
        show={show && !showDeleteOccurrenceModal}
        onClose={onClose}
        title={t("header.editAppointment")}
        footer={
          <Button
            appearance="secondary"
            onClick={onClose}
            style={{ width: "100%" }}
          >
            {t("header.cancel")}
          </Button>
        }
      >
        <p className="recurrenceModalSubtitle">{subtitle}</p>

        <div className="recurrenceActionList">
          <button
            type="button"
            className="recurrenceActionItem"
            onClick={() => onEditSeries(event)}
          >
            <IconCalendarMonth className="recurrenceActionIcon" />
            <span>{editSeriesLabel}</span>
          </button>

          {canEditOccurrences && !isNonRecurring && (
            <button
              type="button"
              className="recurrenceActionItem"
              onClick={() => onEditOccurrences(event)}
            >
              <IconReload className="recurrenceActionIcon" />
              <span>{t("header.editOccurrences")}</span>
            </button>
          )}

          <button
            type="button"
            className="recurrenceActionItem"
            onClick={() => { onClose(); onDeleteSeries(event); }}
          >
            <IconTrash className="recurrenceActionIcon recurrenceActionIconDelete" />
            <span>{deleteSeriesLabel}</span>
          </button>

          {canDeleteOccurrence && !isNonRecurring && (
            <button
              type="button"
              className="recurrenceActionItem"
              onClick={handleDeleteOccurrenceClick}
            >
              <IconTrash className="recurrenceActionIcon recurrenceActionIconDelete" />
              <span>{t("header.deleteOccurrence")}</span>
            </button>
          )}
        </div>
      </Modal>

      <Modal
        align="center"
        closeButton
        id="modal-delete-occurrence"
        show={showDeleteOccurrenceModal}
        onClose={handleCloseDeleteOccurrenceModal}
        title={t("header.whatDoYouWantToDelete")}
        footer={
          <Button
            appearance="secondary"
            onClick={handleCloseDeleteOccurrenceModal}
            style={{ width: "100%" }}
          >
            {t("header.cancel")}
          </Button>
        }
      >
        <div className="recurrenceActionList">
          <button
            type="button"
            className="recurrenceActionItem"
            onClick={() => handleOccurrenceMode("onlyThisDate")}
          >
            <IconTrash className="recurrenceActionIcon recurrenceActionIconDelete" />
            <span>{t("header.deleteOnlyThisDate")}</span>
          </button>

          <button
            type="button"
            className="recurrenceActionItem"
            onClick={() => handleOccurrenceMode("byRange")}
          >
            <IconTrash className="recurrenceActionIcon recurrenceActionIconDelete" />
            <span>{t("header.deleteByRange")}</span>
          </button>

          <button
            type="button"
            className="recurrenceActionItem"
            onClick={() => handleOccurrenceMode("selectedDates")}
          >
            <IconTrash className="recurrenceActionIcon recurrenceActionIconDelete" />
            <span>{t("header.deleteSelectedDates")}</span>
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default RecurrenceActionModal;
